import Header from 'components/headers/bulletins'
import DotsLoading from 'components/loadings/dotsLoading'
import { SectionError } from 'components/states'
import posthog from 'posthog-js'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { getMessagingPersonMessages } from 'services/messagingHistory'
import { ChannelChips } from '../ChannelChips'
import { jalaliDateTime } from '../format'

const statusOf = (error: unknown) =>
    (error as { response?: { status?: number } })?.response?.status ?? null

function MessageCard({ message }: { message: IMessagingMessage }) {
    return (
        <div className=" mb-3 rounded-2xl border border-[#EFEFEF] bg-white p-4 shadow-card">
            <span className=" inline-block rounded-lg bg-[#E8F5EC] px-3 py-1 text-xs text-green-1">
                {message.title}
            </span>
            <p className=" mt-1.5 text-[11px] text-text-secondary">
                {jalaliDateTime(message.at)}
            </p>
            <p
                dir="auto"
                className=" mt-2 whitespace-pre-line text-xs leading-relaxed text-text-body"
            >
                {message.text}
            </p>
            <ChannelChips channels={message.channels} />
        </div>
    )
}

/**
 * One member's timeline: everything Hamyan has told this person, newest first.
 *
 * The name comes through router state when the manager tapped their way here
 * — the list already had it — and from the timeline answer itself otherwise,
 * so a refresh or a shared link still says whose page this is.
 */
export default function AnnouncePersonTimeline() {
    const { id, memberId } = useParams()
    const { state } = useLocation()
    const [name, setName] = useState<string | undefined>(
        (state as { name?: string } | null)?.name
    )

    const [items, setItems] = useState<IMessagingMessage[]>([])
    const [page, setPage] = useState(1)
    const [hasNext, setHasNext] = useState(false)
    const [loading, setLoading] = useState(true)
    const [loadingMore, setLoadingMore] = useState(false)
    const [failed, setFailed] = useState(false)
    const reported = useRef(false)

    const loadPage = useCallback(
        async (pageNumber: number, reset: boolean) => {
            if (!id || !memberId) return
            reset ? setLoading(true) : setLoadingMore(true)
            if (reset) setFailed(false)
            try {
                const data = await getMessagingPersonMessages(
                    id,
                    memberId,
                    pageNumber
                )
                const batch = data?.results ?? []
                if (data?.name) setName((known) => known || data.name)
                setItems((prev) => (reset ? batch : [...prev, ...batch]))
                setHasNext(!!data?.has_next)
                if (reset && !reported.current) {
                    reported.current = true
                    // No name and no message text: this is one member's
                    // correspondence with us.
                    posthog.capture('announce_person_viewed', {
                        moneypool_id: Number(id),
                        member_id: Number(memberId),
                        message_count: batch.length,
                        has_next: !!data?.has_next,
                    })
                }
            } catch (error) {
                if (reset) setItems([])
                setHasNext(false)
                setFailed(true)
                posthog.capture('announce_history_load_failed', {
                    screen: 'person',
                    moneypool_id: Number(id),
                    member_id: Number(memberId),
                    page: pageNumber,
                    status: statusOf(error),
                })
            } finally {
                setLoading(false)
                setLoadingMore(false)
            }
        },
        [id, memberId]
    )

    useEffect(() => {
        loadPage(1, true)
    }, [loadPage])

    const onLoadMore = () => {
        const next = page + 1
        setPage(next)
        loadPage(next, false)
    }

    return (
        <div className=" min-h-screen bg-canvas pb-10">
            <Header title={name || 'اطلاع‌رسانی‌های عضو'} />
            <div className=" px-3 pt-3">
                {loading ? (
                    <DotsLoading />
                ) : failed ? (
                    <SectionError
                        message="پیام‌های این عضو دریافت نشد"
                        onRetry={() => loadPage(1, true)}
                    />
                ) : items.length === 0 ? (
                    <div className=" rounded-2xl border border-[#EFEFEF] bg-white px-4 py-10 text-center text-sm text-text-secondary">
                        پیامی برای این عضو ثبت نشده است
                    </div>
                ) : (
                    <>
                        {items.map((message, index) => (
                            <MessageCard
                                key={`${message.batch_id}-${index}`}
                                message={message}
                            />
                        ))}
                        {hasNext && (
                            <button
                                type="button"
                                onClick={onLoadMore}
                                disabled={loadingMore}
                                className=" my-3 w-full rounded-xl border border-green-1 py-2.5 text-sm text-green-1 disabled:opacity-60"
                            >
                                {loadingMore
                                    ? 'در حال بارگذاری...'
                                    : 'موارد بیشتر'}
                            </button>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}
